import { useEffect, useState } from "react";

export interface DeviceCapabilities {
	isTouchDevice: boolean;
	isCoarsePointer: boolean;
	isMobile: boolean;
	isLowPower: boolean;
	devicePixelRatio: number;
}

function getCapabilities(): DeviceCapabilities {
	if (typeof window === "undefined") {
		return {
			isTouchDevice: false,
			isCoarsePointer: false,
			isMobile: false,
			isLowPower: false,
			devicePixelRatio: 1,
		};
	}

	const isCoarsePointer = window.matchMedia?.("(pointer: coarse)").matches ?? false;
	const isMobile = isCoarsePointer && window.screen.width < 768;
	const deviceMemory =
		"deviceMemory" in navigator
			? (navigator as unknown as { deviceMemory: number }).deviceMemory
			: 8;
	const isLowPower = (navigator.hardwareConcurrency ?? 8) < 4 || deviceMemory < 4;

	return {
		isTouchDevice: isCoarsePointer || "ontouchstart" in window || navigator.maxTouchPoints > 0,
		isCoarsePointer,
		isMobile,
		isLowPower,
		devicePixelRatio: Math.min(window.devicePixelRatio ?? 1, 2),
	};
}

/**
 * Reactive device capability detection.
 * Updates when pointer type or screen size changes.
 */
export function useDeviceCapabilities(): DeviceCapabilities {
	const [caps, setCaps] = useState(getCapabilities);

	useEffect(() => {
		const mql = window.matchMedia("(pointer: coarse)");
		const handler = () => setCaps(getCapabilities());
		mql.addEventListener("change", handler);
		window.addEventListener("resize", handler);
		return () => {
			mql.removeEventListener("change", handler);
			window.removeEventListener("resize", handler);
		};
	}, []);

	return caps;
}

/** Module-level snapshot for non-React code (textures, etc.) */
export const IS_COARSE_POINTER =
	typeof window !== "undefined" && (window.matchMedia?.("(pointer: coarse)").matches ?? false);
